// src/components/TrialBanner.js - Updated to include a link to billing
import React, { useState, useEffect, useContext } from 'react';
import { Box, Alert, Button, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/auth/AuthContext';

const TrialBanner = () => {
  const [trialInfo, setTrialInfo] = useState(null);
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  
  // Check if user is admin (don't show trial banner for admins)
  const isAdmin = currentUser?.role === 'admin';
  
  useEffect(() => {
    // Skip for admin users
    if (isAdmin) return;
    
    const getTrialStatusFromUser = () => {
      if (currentUser?.is_trial) {
        const trialEndDate = new Date(currentUser.trial_end_date);
        const today = new Date();
        const differenceMs = trialEndDate - today;
        const daysRemaining = Math.max(0, Math.ceil(differenceMs / (1000 * 60 * 60 * 24)));
        
        return {
          isTrial: true,
          isExpired: today > trialEndDate,
          daysRemaining: daysRemaining,
          trialEndDate: trialEndDate.toISOString()
        };
      }
      return null;
    };
    
    const getTrialStatusFromStorage = () => {
      try {
        // First check for cached trial info
        const cachedTrialInfo = localStorage.getItem('trialInfo');
        if (cachedTrialInfo) {
          const parsedInfo = JSON.parse(cachedTrialInfo);
          if (parsedInfo && parsedInfo.isTrial) {
            const endDate = new Date(parsedInfo.trialEndDate);
            const today = new Date();
            return {
              isTrial: true,
              isExpired: today > endDate,
              daysRemaining: Math.max(0, Math.ceil((endDate - today) / (1000 * 60 * 60 * 24))),
              trialEndDate: parsedInfo.trialEndDate
            };
          }
        }

        // Then check for _pmt encoded data
        const encodedTrialData = localStorage.getItem('_pmt');
        if (encodedTrialData) {
          try {
            const decoded = JSON.parse(atob(encodedTrialData));
            if (decoded && decoded.isTrial) {
              const endDate = new Date(decoded.endDate);
              const today = new Date();
              return {
                isTrial: true,
                isExpired: today > endDate,
                daysRemaining: Math.max(0, Math.ceil((endDate - today) / (1000 * 60 * 60 * 24))),
                trialEndDate: decoded.endDate
              };
            }
          } catch (e) {
            console.error('Error decoding trial data:', e);
          }
        }
      } catch (e) {
        console.error('Error reading trial info from storage:', e);
      }
      
      return null;
    };
    
    // Try to get trial status from user object first, then from storage
    const trial = getTrialStatusFromUser() || getTrialStatusFromStorage();
    
    if (trial) {
      setTrialInfo(trial);
      
      // Store the trial info for easier access later
      localStorage.setItem('trialInfo', JSON.stringify(trial));
    }
  }, [currentUser, isAdmin]);
  
  // Don't show anything if not on trial or user is admin
  if (!trialInfo || !trialInfo.isTrial || isAdmin) return null;
  
  // Navigate to upgrade page
  const handleUpgrade = () => {
    navigate('/profiles/billing');
  };
  
  return (
    <Box sx={{ mb: 2 }}>
      <Alert 
        severity={trialInfo.isExpired ? "error" : "warning"}
        action={
          <Button color="inherit" size="small" onClick={handleUpgrade}>
            {trialInfo.isExpired ? 'Upgrade Now' : 'View Plans'}
          </Button>
        }
      >
        {trialInfo.isExpired ? (
          <Typography variant="body2">
            Your trial has expired. Upgrade now to restore full access.
          </Typography>
        ) : (
          <Typography variant="body2">
            Your trial will expire in {trialInfo.daysRemaining} day{trialInfo.daysRemaining !== 1 ? 's' : ''}. Upgrade to keep access to all features.
          </Typography>
        )}
      </Alert>
    </Box>
  );
};

export default TrialBanner;